import {
    parseDate,
    parseDateTime,
    type DateValue,
} from '@internationalized/date';

/** The string shapes `DatePickerField` reads and writes. */
export type DatePickerOutputFormat =
    'YYYY-MM-DD' | 'DD/MM/YYYY' | 'YYYY-MM-DDTHH:mm';

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const ISO_DATE_TIME = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;
const DAY_FIRST = /^(\d{2})\/(\d{2})\/(\d{4})$/;

const pad = (part: number, width = 2): string =>
    String(part).padStart(width, '0');

type DateParts = {
    year: string;
    month: string;
    day: string;
    hour: string;
    minute: string;
};

/** The digits of a string in one of the three shapes, else `null`. */
const partsOf = (value: string): DateParts | null => {
    const iso = ISO_DATE_TIME.exec(value) ?? ISO_DATE.exec(value);
    if (iso) {
        const [, year = '', month = '', day = '', hour = '00', minute = '00'] =
            iso;
        return { year, month, day, hour, minute };
    }
    const dayFirst = DAY_FIRST.exec(value);
    if (dayFirst) {
        const [, day = '', month = '', year = ''] = dayFirst;
        return { year, month, day, hour: '00', minute: '00' };
    }
    return null;
};

/**
 * The field's value for a string in any of the three shapes (whatever the
 * output format), or `null` for a blank or anything that is not a real date.
 *
 * Parsed without dayjs on purpose: dayjs builds through `new Date(y, …)`,
 * which reads a year below 100 as 19yy, so `0002-06-15` (React Aria's value
 * while the owner types the year 2028 digit by digit) did not round-trip and
 * the field reset mid-typing.
 */
export const parseDatePickerValue = (
    value: string,
    outputFormat: DatePickerOutputFormat,
): DateValue | null => {
    if (!value) {
        return null;
    }
    const parts = partsOf(value);
    if (!parts) {
        return null;
    }
    const { year, month, day, hour, minute } = parts;
    if (Number(day) < 1 || Number(hour) > 23 || Number(minute) > 59) {
        return null;
    }
    try {
        const iso = `${year}-${month}-${day}`;
        return outputFormat === 'YYYY-MM-DDTHH:mm'
            ? parseDateTime(`${iso}T${hour}:${minute}`)
            : parseDate(iso);
    } catch {
        // A day the month does not have (31 February), or a month past 12.
        return null;
    }
};

/**
 * The string `onChange` reports for the field's value: the output format
 * with the year padded to four digits, or `''` for no value and for one
 * whose parts are not whole numbers. React Aria reports every complete
 * value while a year is typed or deleted digit by digit (2, 20, 202, 2028),
 * so each must come out as a date `parseDatePickerValue` reads back; an
 * unpadded year once came out as dayjs's "Invalid Date".
 */
export const formatDatePickerValue = (
    value: DateValue | null,
    outputFormat: DatePickerOutputFormat,
): string => {
    if (!value) {
        return '';
    }
    const hasTime = 'hour' in value && 'minute' in value;
    const hour = hasTime ? value.hour : 0;
    const minute = hasTime ? value.minute : 0;
    if (
        ![value.year, value.month, value.day, hour, minute].every((part) =>
            Number.isInteger(part),
        )
    ) {
        return '';
    }
    const year = pad(value.year, 4);
    const month = pad(value.month);
    const day = pad(value.day);
    switch (outputFormat) {
        case 'DD/MM/YYYY':
            return `${day}/${month}/${year}`;
        case 'YYYY-MM-DDTHH:mm':
            return `${year}-${month}-${day}T${pad(hour)}:${pad(minute)}`;
        case 'YYYY-MM-DD':
            return `${year}-${month}-${day}`;
    }
};
